import { useCallback } from 'react';

import { useToast } from 'core/components/toast';
import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { OPTION_NO_LONGER_ON_OFFER_MESSAGE } from 'core/store/offer-store/offer-store';

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Runs the active-policy match requestBind warns on before Staff hands a customer a link that ends
 * at checkout, so the agent sees the "already has an active policy" dialog here rather than the
 * customer dead-ending (#34591). Resolves true to go ahead with the share. Fails open when the
 * check cannot run, since the bind-time check still stands.
 */
const useActivePolicyGate = () => {
  const toast = useToast();
  const { offer: store } = useStore();
  const {
    user: { username }
  } = useSession();

  return useCallback(
    async ({ policyType, linkName }) => {
      const option = store.offer.options?.find((o) => o.type === policyType);
      if (!option) {
        toast.notify({ type: 'error', message: OPTION_NO_LONGER_ON_OFFER_MESSAGE });
        return false;
      }

      let match;
      try {
        match = await store.checkMatchingActivePolicies(option.id);
      } catch (error) {
        track(`${capitalize(linkName)} active-policy check failed`, {
          user: username,
          offerId: store.offer.id,
          error: error.message
        });
        return true;
      }
      if (!match?.foundMatchingActivePolicy) {
        return true;
      }

      const sharedAnyway = await store.warnActivePolicyMatchBeforeShare({
        matchingPolicyIds: match.matchingPolicyIds,
        linkName
      });
      track(`${capitalize(linkName)} matched active policy`, {
        user: username,
        offerId: store.offer.id,
        optionId: option.id,
        matchingPolicyIds: match.matchingPolicyIds,
        sharedAnyway
      });
      return sharedAnyway;
    },
    [store, toast, username]
  );
};

export default useActivePolicyGate;
